// workaround, this should be removed from the Pro's concern
import "@similarweb/pro-education-bar/style";
import React, { type JSX } from "react";

import isPropValid from "@emotion/is-prop-valid";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { UIRouter, UIView } from "@uirouter/react";
import { enableMapSet } from "immer";
import * as ReactDOMClient from "react-dom/client";
import { Provider } from "react-redux";
// @ts-ignore: no types for this package
import singleSpaReact from "single-spa-react";
import { StyleSheetManager } from "styled-components";
import type { StyledTarget } from "styled-components/dist/types";

import { TranslateProvider } from "@similarweb/i18n-provider";
import type { Track } from "@similarweb/tracking-provider";
import { TrackingProvider } from "@similarweb/tracking-provider";

import { FirstLandModalProvider } from "app/pages/activation/components/FirstLandModalProvider/FirstLandModalProvider";
import { SecondLandExtensionModal } from "app/components/SecondLandExtensionModal/SecondLandExtensionModal";
import { getStateForDevTools } from "app/services/dev-toggle/devToggle";
import { allTrackers } from "app/services/track/track";
import AllContexts from "pro-features/pages/workspace/common components/AllContexts";

import { appInit } from "./init/app.init";
import { initGoogleTag } from "./init/initGoogleTag";
import { proNavigator } from "./ProNavigator";
import queryClient from "./query-client";
import { initRouter, disposeRouter } from "./router/router";

//Do Not Remove - this file is used to import the widget files so they would self register (what used to be in "those we don't talk about...")
//todo: this is problematic due to tree-shaking
import "app/components/widget/widget-types/WidgetImportFiles";

let router: any;
const AppRouter = (): JSX.Element => {
    return (
        <UIRouter router={router}>
            <UIView />
        </UIRouter>
    );
};

function shouldForwardProp(propName: string, target: StyledTarget<any>): boolean {
    if (typeof target === "string") {
        return isPropValid(propName);
    }

    return true;
}

enableMapSet();

const showDevTools = getStateForDevTools();

const App = (props: any): JSX.Element => {
    return (
        <QueryClientProvider client={queryClient}>
            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                <Provider store={props.store}>
                    <TranslateProvider
                        translate={window.i18n?.t}
                        baseUrl={window.i18n?.baseUrl}
                        env={window.i18n?.env}
                    >
                        <TrackingProvider track={allTrackers.trackEvent.bind(allTrackers) as Track}>
                            <AllContexts linkFn={proNavigator.href}>
                                <FirstLandModalProvider>
                                    <AppRouter />
                                </FirstLandModalProvider>
                                <SecondLandExtensionModal />
                            </AllContexts>
                        </TrackingProvider>
                    </TranslateProvider>
                </Provider>
            </StyleSheetManager>
            {showDevTools ? (
                <ReactQueryDevtools
                    position="right"
                    initialIsOpen={false}
                />
            ) : null}
        </QueryClientProvider>
    );
};

function domElementGetter(): HTMLElement {
    let el = document.getElementById("react-app");
    if (!el) {
        el = document.createElement("div");
        el.id = "react-app";
        document.body.appendChild(el);
    }
    return el;
}

const reactLifecycles = singleSpaReact({
    suppressComponentDidCatchWarning: true,
    React,
    ReactDOMClient,
    rootComponent: App,
    domElementGetter,
});

export const bootstrap = async (_props: any): Promise<void> => {};

export const mount = (props: any) => {
    return Promise.resolve().then(async () => {
        $(".app-loader").hide();
        const { settings, categories, countries, industries } = props.startup;

        window.similarweb = window.similarweb || {};
        window.similarweb.settings = settings;
        window.similarweb.config = {
            userData: {},
            ...window.similarweb.config,
            categories,
            countries: countries.countriesClient,
            mobileUsersCountries: countries.mobileUsersCountries,
            regions: countries.regions,
            industries,
        };

        await initGoogleTag();
        router = initRouter();
        await appInit(router);
        await reactLifecycles.mount(props);
    });
};

export const unmount = (props: any) => {
    return Promise.resolve().then(async () => {
        const rootElement = domElementGetter();
        disposeRouter();
        await reactLifecycles.unmount(props);
        rootElement?.parentNode?.removeChild(rootElement);
    });
};
